import { PaymentStatusUI } from '~entities/payment';

import { MoneyPh } from '~shared/ui/money-ph';

import { createColumnHelper } from '@tanstack/react-table';

import type { PaymentTableData } from '../model/payments-table-data';

export enum ColumnId {
  STATUS = 'status',
  TRANSACTION_ID = 'transactionId',
  AGREEMENT_ID = 'agreementId',
  POS = 'pos',
  FINANCED_AMOUNT = 'financedAmount',
  ZERO_RATE_SUBSIDY = 'zeroRateSubsidy',
  SETTLED_AMOUNT = 'settledAmount',
}

type ColumnOption = { id: ColumnId; label: string };

export const COLUMN_OPTIONS: ColumnOption[] = [
  { id: ColumnId.STATUS, label: 'Status' },
  { id: ColumnId.TRANSACTION_ID, label: 'Transaction ID' },
  { id: ColumnId.AGREEMENT_ID, label: 'Agreement ID' },
  { id: ColumnId.POS, label: 'Store name' },
  { id: ColumnId.FINANCED_AMOUNT, label: 'Financed amount' },
  { id: ColumnId.ZERO_RATE_SUBSIDY, label: 'Subsidy' },
  { id: ColumnId.SETTLED_AMOUNT, label: 'Settled amount' },
];

export const ALL_COLUMN_IDS = COLUMN_OPTIONS.map((option) => option.id);

export const SORT_OPTIONS: ColumnOption[] = [
  { id: ColumnId.STATUS, label: 'Status' },
  { id: ColumnId.SETTLED_AMOUNT, label: 'Settled amount' },
];

const columnHelper = createColumnHelper<PaymentTableData>();

type Props = {
  name?: string;
  merchantName?: string;
};

export const StoreInfo = ({ name, merchantName }: Props) => {
  if (!name) {
    return <span>–</span>;
  }
  return (
    <div>
      <span
        className={`
          inline-block max-w-full overflow-hidden text-ellipsis
          whitespace-nowrap
        `}
        title={name}
      >
        {name}
      </span>
      <br />
      <span
        className={`
          inline-block max-w-full overflow-hidden text-sm text-ellipsis
          whitespace-nowrap text-black/50
        `}
        title={merchantName}
      >
        {merchantName}
      </span>
    </div>
  );
};

export const columns = [
  columnHelper.accessor(
    ({ paymentStatus, paymentTimestampUTC }) => ({
      paymentStatus,
      paymentTimestampUTC,
    }),
    {
      id: ColumnId.STATUS,
      meta: { width: 180, sortable: true },
      cell: (data) => <PaymentStatusUI {...data.getValue()} />,
      header: 'Status',
    }
  ),
  columnHelper.accessor((row) => row.transactionId, {
    id: ColumnId.TRANSACTION_ID,
    meta: { minWidth: 150, width: '15%' },
    cell: (data) => (
      <span className="max-w-full">{data.getValue() || '–'}</span>
    ),
    header: () => <>Transaction&nbsp;ID</>,
  }),
  columnHelper.accessor((row) => row.agreementId, {
    id: ColumnId.AGREEMENT_ID,
    meta: { minWidth: 150, width: '15%' },
    cell: (data) => (
      <span className="max-w-full">{data.getValue() || '–'}</span>
    ),
    header: () => <>Agreement&nbsp;ID</>,
  }),
  columnHelper.accessor((row) => row.pos, {
    id: ColumnId.POS,
    meta: { minWidth: 300, width: '30%' },
    cell: (data) => <StoreInfo {...data.getValue()} />,
    header: () => <span>Store name</span>,
  }),
  columnHelper.accessor((row) => row.financedAmount, {
    id: ColumnId.FINANCED_AMOUNT,
    meta: { minWidth: 130, align: 'right' },
    cell: (data) => <MoneyPh value={data.getValue()} />,
    header: () => <span>Financed amount</span>,
  }),
  columnHelper.accessor((row) => row.subsidyAmount, {
    id: ColumnId.ZERO_RATE_SUBSIDY,
    meta: { minWidth: 130, align: 'right' },
    cell: (data) => {
      const subsidy = data.getValue();
      return (
        <>
          {subsidy ? '–' : ''}
          <MoneyPh value={subsidy} />
        </>
      );
    },
    header: () => <span>Subsidy</span>,
  }),
  columnHelper.accessor((row) => row.settledAmount, {
    id: ColumnId.SETTLED_AMOUNT,
    meta: { minWidth: 130, align: 'right', sortable: true },
    cell: (data) => <MoneyPh value={data.getValue()} />,
    header: () => <span>Settled amount</span>,
  }),
];
